import React, { forwardRef } from 'react';
import Spinner from './Spinner';

export type InputVariant = 'default' | 'error' | 'success';
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: InputVariant;
  size?: InputSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  error?: boolean;
}

export interface InputRef {
  focus: () => void;
  blur: () => void;
  select: () => void;
}

const Input = forwardRef<InputRef, InputProps>(({
  variant = 'default',
  size = 'md',
  leftIcon,
  rightIcon,
  isLoading = false,
  error = false,
  className = '',
  disabled,
  ...props
}, ref) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
    select: () => inputRef.current?.select(),
  }));

  // Size classes
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-sm',
    md: 'px-3 py-2 text-base',
    lg: 'px-4 py-3 text-lg'
  };

  // Variant classes
  const variantClasses = {
    default: {
      base: 'border-border-medium bg-bg-secondary text-text-primary',
      focus: 'focus:ring-2 focus:ring-action-key-default focus:border-action-key-default',
      placeholder: 'placeholder:text-content-tertiary'
    },
    error: {
      base: 'border-danger-border bg-danger-light text-text-primary',
      focus: 'focus:ring-2 focus:ring-danger-border focus:border-danger-border',
      placeholder: 'placeholder:text-content-tertiary'
    },
    success: {
      base: 'border-success-border bg-success-light text-text-primary',
      focus: 'focus:ring-2 focus:ring-success-border focus:border-success-border',
      placeholder: 'placeholder:text-content-tertiary'
    }
  };

  // Determine the variant based on error prop
  const currentVariant = error ? 'error' : variant;

  const baseClasses = [
    'w-full',
    'border',
    'rounded-md',
    'shadow-sm',
    'transition-all',
    'duration-200',
    'font-manrope',
    'font-medium',
    'focus:outline-none',
    'disabled:cursor-not-allowed',
    'disabled:opacity-50',
    'disabled:bg-disabled-fill'
  ];

  const classes = [
    ...baseClasses,
    sizeClasses[size],
    variantClasses[currentVariant].base,
    variantClasses[currentVariant].focus,
    variantClasses[currentVariant].placeholder,
    className
  ].filter(Boolean).join(' ');

  const hasIcons = !!(leftIcon ?? rightIcon ?? isLoading);

  if (hasIcons) {
    return (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <div className="w-5 h-5 text-content-tertiary">
              {leftIcon}
            </div>
          </div>
        )}
        
        <input
          ref={inputRef}
          className={`${classes} ${leftIcon ? 'pl-10' : ''} ${(rightIcon ?? isLoading) ? 'pr-10' : ''}`}
          disabled={disabled ?? isLoading}
          {...props}
        />
        
        {(rightIcon ?? isLoading) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <div className="w-5 h-5 text-content-tertiary">
              {isLoading ? (
                <Spinner size="sm" variant="muted" />
              ) : rightIcon}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      className={classes}
      disabled={disabled ?? isLoading}
      {...props}
    />
  );
});

Input.displayName = 'Input';

export default Input;